import type { AxiosError } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AdvocatePageType, type Org } from '../../../enum';
import { APIStatus, AppCode } from '../../../enum';
import type { BaseDashboardParams } from '../../../models/dashboard';
import type { LineChartData } from '../../../models/echarts';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { dateTimeFilterFormat } from '../program-dashboard/program-dashboard.helper';
import { type DashboardBody, getFiltersInfo } from '../program-dashboard/programDashboardSlice';

const { logError } = RaygunErrorHandlerService();

export type AdvocateSearchResult = {
  advocate_id: string;
  available: boolean;
  in_chat: boolean;
  name: string;
  img_url: string | null;
  lived_experiences_aliases: string[];
  spoken_languages_aliases: string[];
  community_names: string[];
  service_provider_organization_id: string | null;
  service_provider_organization_name: string | null;
  service_provider_organization_internal: boolean | null;
  service_provider_title: string | null;
  service_title: string | null;
  service_description: string | null;
  org_name: string;
  org_code: Org;
};

type AdvocatesOnlineDashboardData = {
  advocates_online_hourly: LineChartData[];
  online_advocates: AdvocateSearchResult[];
};

type AdvocatesOnlineDashboardSliceType = {
  onlineDashboardParams: BaseDashboardParams;
  onlineDashboardData: AdvocatesOnlineDashboardData;
  advocateOnlineApiStatus: APIStatus;
  weekdayWeekendHoursApiStatus: APIStatus;
  weekdayWeekendHoursCSV: string;
  filtersInfo: string;
};

const initialState: AdvocatesOnlineDashboardSliceType = {
  onlineDashboardParams: {
    app_code: AppCode.TalkingStick,
    start_date: dayjs().subtract(1, 'week'),
    end_date: dayjs(),
    selected_org_code: 'allOrgs',
  },
  onlineDashboardData: {
    advocates_online_hourly: [],
    online_advocates: [],
  },
  advocateOnlineApiStatus: APIStatus.IDLE,
  weekdayWeekendHoursApiStatus: APIStatus.IDLE,
  weekdayWeekendHoursCSV: '',
  filtersInfo: '',
};

export const loadAdvocateOnlineDashboard = createAsyncThunk<
  AdvocatesOnlineDashboardData,
  AdvocatePageType,
  AsyncThunkConfig
>('advocatesOnlineDashboard/loadAdvocateOnlineDashboard', async (advocate_page_type, thunkAPI) => {
  try {
    const { start_date, end_date, selected_org_code } =
      thunkAPI.getState().advocatesOnlineDashboardSlice.onlineDashboardParams;
    const body: DashboardBody = {
      start_date: start_date.startOf('day').format(dateTimeFilterFormat),
      end_date: end_date.endOf('day').format(dateTimeFilterFormat),
      selected_org_code: selected_org_code === 'allOrgs' ? null : selected_org_code,
      for_leps: advocate_page_type === AdvocatePageType.PeerAdvocate,
      for_service_providers: advocate_page_type === AdvocatePageType.ServiceProvider,
      app_code: AppCode.TalkingStick,
    };
    const response = (await axios.post('v0_advocate_online_dashboard', body)) as AdvocatesOnlineDashboardData;
    const tsOrgs = thunkAPI.getState().orgsSlice.orgsForAdminDic.ts;
    const filtersInfo = getFiltersInfo(body, tsOrgs);
    thunkAPI.dispatch(setFiltersInfo(filtersInfo));
    return response ?? initialState.onlineDashboardData;
  } catch (e) {
    logError(e, ['advocateOnlineDashboardSlice', 'loadAdvocateOnlineDashboard']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const getWeekdayWeekendHours = createAsyncThunk<string, AdvocatePageType, AsyncThunkConfig>(
  'advocatesOnlineDashboard/getWeekdayWeekendHours',
  async (advocate_page_type, thunkAPI) => {
    try {
      const { start_date, end_date, selected_org_code } =
        thunkAPI.getState().advocatesOnlineDashboardSlice.onlineDashboardParams;
      const body: DashboardBody = {
        start_date: start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: selected_org_code === 'allOrgs' ? null : selected_org_code,
        for_leps: advocate_page_type === AdvocatePageType.PeerAdvocate,
        for_service_providers: advocate_page_type === AdvocatePageType.ServiceProvider,
        app_code: AppCode.TalkingStick,
      };
      const response = (await axios.post('v0_advocate_weekend_weekday_hours', body)) as string; // csv
      return response ?? '';
    } catch (e) {
      logError(e, ['advocateOnlineDashboardSlice', 'getWeekdayWeekendHours']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const advocatesOnlineDashboardSlice = createSlice({
  name: 'advocatesOnlineDashboard',
  initialState,
  reducers: {
    handleParamsChange: (state, action: PayloadAction<Partial<BaseDashboardParams>>) => {
      state.onlineDashboardParams = { ...state.onlineDashboardParams, ...action.payload };
    },
    setFiltersInfo: (state, action: PayloadAction<string>) => {
      state.filtersInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAdvocateOnlineDashboard.pending, (state, _action) => {
        state.advocateOnlineApiStatus = APIStatus.PENDING;
      })
      .addCase(loadAdvocateOnlineDashboard.fulfilled, (state, action) => {
        state.onlineDashboardData = action.payload;
        state.advocateOnlineApiStatus = APIStatus.FULFILLED;
      })
      .addCase(loadAdvocateOnlineDashboard.rejected, (state, _action) => {
        state.advocateOnlineApiStatus = APIStatus.ERROR;
      })
      .addCase(getWeekdayWeekendHours.pending, (state, _action) => {
        state.weekdayWeekendHoursApiStatus = APIStatus.PENDING;
      })
      .addCase(getWeekdayWeekendHours.fulfilled, (state, action) => {
        state.weekdayWeekendHoursCSV = action.payload;
        state.weekdayWeekendHoursApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getWeekdayWeekendHours.rejected, (state, _action) => {
        state.weekdayWeekendHoursApiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleParamsChange, setFiltersInfo } = advocatesOnlineDashboardSlice.actions;
